import { motion, useSpring, useMotionValue } from "motion/react";
import { useState, useEffect, useRef } from "react";

export function CustomCursor() {
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hoverText, setHoverText] = useState<string | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const isVisibleRef = useRef(false);
  const lastTargetRef = useRef<EventTarget | null>(null);

  // Use motion values for 60fps+ raw performance (no React render delay)
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  // Use stable spring config to prevent "snapping" when properties change
  const cursorXSpring = useSpring(cursorX, { damping: 35, stiffness: 350, mass: 0.5 });
  const cursorYSpring = useSpring(cursorY, { damping: 35, stiffness: 350, mass: 0.5 });

  useEffect(() => {
    const touchQuery = window.matchMedia('(pointer: coarse)');
    setIsTouchDevice(touchQuery.matches);

    const handleTouchChange = (e: MediaQueryListEvent) => {
      setIsTouchDevice(e.matches);
    };

    touchQuery.addEventListener('change', handleTouchChange);
    
    const updatePosition = (e: MouseEvent) => {
      if (touchQuery.matches) return;
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      
      if (!isVisibleRef.current) {
        isVisibleRef.current = true;
        setIsVisible(true);
      }
    };
    
    const updateHoverState = (e: MouseEvent) => {
      if (e.target === lastTargetRef.current) return;
      lastTargetRef.current = e.target;

      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      
      const sikeElement = target.closest('.hover-sike');
      if (sikeElement) {
        setHoverText("SIKE");
        setIsHovering(true);
      } else {
        setHoverText(null);
        const isOverIframe = target.tagName === 'IFRAME';
        const hoverable = target.closest('a, button, .hover-target, [role="button"]');
        setIsHovering(!!hoverable || isOverIframe);
      }
    };

    const handleMouseLeave = () => {
      isVisibleRef.current = false;
      setIsVisible(false);
    };
    const handleMouseEnter = () => {
      isVisibleRef.current = true;
      setIsVisible(true);
    };

    window.addEventListener('mousemove', updatePosition, { passive: true });
    window.addEventListener('mouseover', updateHoverState, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    document.addEventListener('mouseenter', handleMouseEnter, { passive: true });

    return () => {
      touchQuery.removeEventListener('change', handleTouchChange);
      window.removeEventListener('mousemove', updatePosition);
      window.removeEventListener('mouseover', updateHoverState);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [cursorX, cursorY]);

  if (!isVisible || isTouchDevice) return null;

  return (
    <>
      {/* Outer Circle - Filled white with mix-blend-difference */}
      <motion.div
        className="fixed top-0 left-0 w-[28px] h-[28px] rounded-full pointer-events-none z-[9999] mix-blend-difference flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: '#FEFDF9',
          x: cursorXSpring,
          y: cursorYSpring,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          scale: isHovering ? 1.42857 : 1,
          opacity: 1,
        }}
        transition={{ type: "tween", ease: "backOut", duration: 0.15 }}
      >
        {hoverText && (
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[#f97316] font-bold text-[8px] tracking-widest"
          >
            {hoverText}
          </motion.span>
        )}
      </motion.div>
      {/* Inner Dot - White with mix-blend-difference (appears black over the outer circle) */}
      <motion.div
        className="fixed top-0 left-0 w-[12px] h-[12px] rounded-full pointer-events-none z-[9999] mix-blend-difference"
        style={{
          backgroundColor: '#FEFDF9',
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          scale: isHovering ? 0 : 1,
        }}
        transition={{ type: "tween", ease: "linear", duration: 0 }}
      />
    </>
  );
}
